import { cn } from "@/lib/formatters";

export interface Filters {
  months: number;
  strikingOnly: boolean;
  posMin: number;
  posMax: number;
  minImpressions: number;
  sort: "impressions" | "position" | "ctr";
}

interface FiltersBarProps {
  value: Filters;
  onChange: (v: Filters) => void;
  onRefresh: () => void;
  refreshing: boolean;
}

const monthOptions = [3, 6, 12, 16];

export function FiltersBar({ value, onChange, onRefresh, refreshing }: FiltersBarProps) {
  const setPatch = (patch: Partial<Filters>) => onChange({ ...value, ...patch });

  return (
    <div className="surface p-4 flex flex-wrap items-end gap-4">
      <FilterGroup label="range">
        <div className="flex gap-1">
          {monthOptions.map((m) => (
            <button
              key={m}
              onClick={() => setPatch({ months: m })}
              className={cn(
                "px-2.5 py-1 rounded-sm font-mono text-xs uppercase tracking-wider transition-colors",
                value.months === m
                  ? "bg-primary/15 text-primary border border-primary/40"
                  : "bg-bg-elev text-muted border border-border hover:text-text",
              )}
            >
              {m}mo
            </button>
          ))}
        </div>
      </FilterGroup>

      <FilterGroup label="position">
        <div className="flex items-center gap-2 font-mono text-xs">
          <input
            type="number"
            min={1}
            max={100}
            value={value.posMin}
            onChange={(e) => setPatch({ posMin: Number(e.target.value) || 1 })}
            className="w-16 px-2 py-1 bg-bg-elev border border-border rounded-sm text-text tabular-nums"
          />
          <span className="text-muted">–</span>
          <input
            type="number"
            min={1}
            max={100}
            value={value.posMax}
            onChange={(e) => setPatch({ posMax: Number(e.target.value) || 100 })}
            className="w-16 px-2 py-1 bg-bg-elev border border-border rounded-sm text-text tabular-nums"
          />
        </div>
      </FilterGroup>

      <FilterGroup label="min impr">
        <input
          type="number"
          min={0}
          step={50}
          value={value.minImpressions}
          onChange={(e) => setPatch({ minImpressions: Number(e.target.value) || 0 })}
          className="w-24 px-2 py-1 bg-bg-elev border border-border rounded-sm text-text font-mono text-xs tabular-nums"
        />
      </FilterGroup>

      <FilterGroup label="sort">
        <select
          value={value.sort}
          onChange={(e) => setPatch({ sort: e.target.value as Filters["sort"] })}
          className="px-2 py-1 bg-bg-elev border border-border rounded-sm text-text font-mono text-xs uppercase tracking-wider"
        >
          <option value="impressions">impressions</option>
          <option value="position">position</option>
          <option value="ctr">ctr</option>
        </select>
      </FilterGroup>

      <label className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-muted select-none">
        <input
          type="checkbox"
          checked={value.strikingOnly}
          onChange={(e) => setPatch({ strikingOnly: e.target.checked })}
          className="accent-primary"
        />
        striking-only
      </label>

      <div className="ml-auto">
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className={cn(
            "px-3 py-1.5 rounded-sm font-mono text-xs uppercase tracking-wider border transition-colors",
            "border-border text-muted hover:text-text hover:border-primary/30",
            "disabled:opacity-40 disabled:cursor-not-allowed",
          )}
        >
          {refreshing ? "…" : "↻ refresh"}
        </button>
      </div>
    </div>
  );
}

function FilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="path-chip">{label}</span>
      {children}
    </div>
  );
}
